import { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CreateTransaction } from "../../services/transaction.services";
import { getUserInfo } from "../../services/account-setting.services";
import { CustomerList } from "../../services/user.services";
import { getUser } from "../../../../utils/commonfunction";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { useNavigate } from "react-router-dom";

function OfflinePayment() {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Offer percentage loaded from business profile
  const storedUser = getUser("business");
  const initialOffer = storedUser?.offer_percentage ? parseFloat(storedUser.offer_percentage) : 10;
  const [offerPercentage, setOfferPercentage] = useState(initialOffer);

  // Customer selection & search states
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Manual customer input states
  const [showManualFields, setShowManualFields] = useState(false);
  const [manualCustomer, setManualCustomer] = useState({ name: "", email: "", phone: "" });
  const [manualError, setManualError] = useState("");

  // Fetch business profile to ensure latest offer_percentage
  useEffect(() => {
    const fetchBusinessProfile = async () => {
      try {
        const res = await getUserInfo();
        if (res?.success && res?.data?.offer_percentage) {
          const pct = parseFloat(res.data.offer_percentage);
          if (!isNaN(pct)) {
            setOfferPercentage(pct);
          }
        }
      } catch (e) {
        console.error("Failed to load business profile for offer percentage:", e);
      }
    };
    fetchBusinessProfile();
  }, []);

  // Fetch non-business customers based on search
  useEffect(() => {
    let isMounted = true;
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const res = await CustomerList(searchQuery);
        if (isMounted && res?.success && Array.isArray(res?.data)) {
          setCustomers(res.data);
        }
      } catch (e) {
        console.error("Failed to fetch customer list:", e);
      } finally {
        if (isMounted) setLoadingCustomers(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchCustomers();
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(debounceTimer);
    };
  }, [searchQuery]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validationSchema = Yup.object({
    amount: Yup.number()
      .required("Bill amount is required")
      .min(1, "Amount must be greater than 0"),
  });

  const formik = useFormik({
    initialValues: {
      amount: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setManualError("");

      // Guest details are now mandatory
      if (!selectedCustomer && !showManualFields) {
        setManualError("Please add guest details before submitting.");
        setSubmitting(false);
        return;
      }

      // Validate manual inputs if manual mode is active
      if (showManualFields && !selectedCustomer) {
        if (!manualCustomer.email.trim()) {
          setManualError("Guest email is required");
          setSubmitting(false);
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(manualCustomer.email.trim())) {
          setManualError("Please enter a valid guest email");
          setSubmitting(false);
          return;
        }
      }

      const totalAmount = parseFloat(values.amount) || 0;
      const discountAmount = totalAmount * (offerPercentage / 100);
      const netPay = totalAmount - discountAmount;

      const customerName = selectedCustomer
        ? selectedCustomer.fullName || selectedCustomer.name || selectedCustomer.email
        : manualCustomer.name.trim();

      const customerEmail = selectedCustomer
        ? selectedCustomer.email
        : manualCustomer.email.trim();

      const customerPhone = selectedCustomer
        ? selectedCustomer.phone_number?.fullNumber || selectedCustomer.phone_number?.phone
        : manualCustomer.phone.trim();

      const data = {
        totalAmount: totalAmount,
        offerPercentage: offerPercentage,
        netPay: netPay,
        discountAmount: discountAmount,
        userID: selectedCustomer?._id || null,
        userName: customerName || "Walk-in Customer",
        userEmail: customerEmail || null,
        customerPhone: customerPhone || null,
      };

      try {
        const res = await CreateTransaction(data);
        if (res?.success) {
          Swal.fire({
            icon: "success",
            text: res?.message || "Offline payment recorded successfully!",
            ...SWAL_SETTINGS,
          });
          navigate(-1);
        } else {
          Swal.fire({
            icon: "error",
            text: res?.data || res?.message || "Failed to create transaction",
            ...SWAL_SETTINGS,
          });
        }
      } catch (err) {
        console.error("Error creating transaction:", err);
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: err?.response?.data?.message || "Something went wrong!",
          ...SWAL_SETTINGS,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const billAmount = formik.values.amount ? parseFloat(formik.values.amount) : 0;
  const discount = billAmount * (offerPercentage / 100);
  const toPay = billAmount - discount;

  return (
    <div className="main-content">
      <div className="Transactions-part">
        <div className="payment-card-box">
          <div className="d-flex flex-column align-items-center gap-3">
            <div className="payment-card-content" style={{ width: "100%", maxWidth: "560px" }}>
              {/* Back link */}
              <div className="d-flex align-items-center mb-3">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 text-dark"
                  onClick={() => navigate(-1)}
                >
                  <i className="fi fi-rr-arrow-small-left fs-4"></i>
                  <span className="fw-semibold">Back</span>
                </button>
              </div>

              <form onSubmit={formik.handleSubmit}>
                {/* Customer selection section */}
                <div className="mb-4" ref={dropdownRef}>
                  <label className="form-label small fw-bold text-dark mb-1">
                    Add Guest Details <span className="text-danger">*</span>
                  </label>

                  {/* Selected Customer View */}
                  {selectedCustomer ? (
                    <div
                      className="p-3 rounded-3 border d-flex justify-content-between align-items-center gap-3"
                      style={{ backgroundColor: "#FFF9F5", borderColor: "#F37A20" }}
                    >
                      <div className="d-flex align-items-center gap-3 flex-grow-1 overflow-hidden">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                          style={{ width: "40px", height: "40px", backgroundColor: "#F37A20", fontSize: "16px" }}
                        >
                          {(
                            selectedCustomer.fullName ||
                            selectedCustomer.name ||
                            selectedCustomer.email ||
                            "C"
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <div className="overflow-hidden">
                          <h6 className="mb-0 fw-bold text-dark text-truncate">
                            {selectedCustomer.fullName || selectedCustomer.name || "App Customer"}
                          </h6>
                          <small className="text-muted d-block text-truncate" style={{ fontSize: "12px" }}>
                            {selectedCustomer.email ||
                              selectedCustomer.phone_number?.fullNumber ||
                              "No contact info"}
                          </small>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm flex-shrink-0"
                        style={{
                          background: "#f0f0f0",
                          color: "#555",
                          border: "none",
                          borderRadius: "20px",
                          fontWeight: 600,
                          padding: "5px 16px",
                          whiteSpace: "nowrap",
                          fontSize: "13px",
                        }}
                        onClick={() => {
                          setSelectedCustomer(null);
                          setSearchQuery("");
                        }}
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    /* Customer Search Bar */
                    <div className="position-relative">
                      <div className="input-group">
                        <span className="input-group-text bg-white border-end-0 text-muted">
                          <i className="fi fi-rr-search"></i>
                        </span>
                        <input
                          type="text"
                          autoComplete="off"
                          className="form-control border-start-0 ps-0"
                          placeholder="Search customer by name, email, or phone..."
                          value={searchQuery}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSearchQuery(val);
                            setDropdownOpen(val.trim().length > 0);
                          }}
                          onFocus={() => {
                            if (searchQuery.trim().length > 0) setDropdownOpen(true);
                          }}
                        />
                        {searchQuery && (
                          <button
                            type="button"
                            className="btn btn-outline-secondary border-start-0"
                            onClick={() => setSearchQuery("")}
                          >
                            <i className="fi fi-rr-cross-small"></i>
                          </button>
                        )}
                      </div>

                      {/* Dropdown Customer List — only shows when user has typed */}
                       {dropdownOpen && searchQuery.trim().length > 0 && (
                        <div
                          className="position-absolute w-100 bg-white border rounded-3 shadow-lg mt-1 p-2"
                          style={{ zIndex: 1050, maxHeight: "240px", overflowY: "auto", overflowX: "hidden", paddingRight: "2px" }}
                        >
                          {loadingCustomers ? (
                            <div className="p-3 text-center text-muted small">Loading customers...</div>
                          ) : customers.length > 0 ? (
                            <>
                              <div className="text-muted px-2 py-1 small fw-bold" style={{ fontSize: "11px" }}>
                                Registered Customers ({customers.length})
                              </div>
                              {customers.map((cust) => {
                                const displayName = cust.fullName || cust.name || cust.email || "Customer";
                                return (
                                  <div
                                    key={cust._id}
                                    className="px-2 py-2 rounded-2 d-flex align-items-center gap-2 cp"
                                    style={{ cursor: "pointer", transition: "background 0.15s" }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = "#FFF4EB"}
                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                  >
                                    <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                                      <div
                                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                                        style={{
                                          width: "34px",
                                          height: "34px",
                                          backgroundColor: "#F37A20",
                                          fontSize: "13px",
                                        }}
                                      >
                                        {displayName.charAt(0).toUpperCase()}
                                      </div>
                                      <div style={{ minWidth: 0 }}>
                                        <div className="fw-semibold text-dark small text-truncate">{displayName}</div>
                                        <div className="text-muted text-truncate" style={{ fontSize: "11px" }}>
                                          {cust.email || cust.phone_number?.fullNumber || ""}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-sm flex-shrink-0"
                                      style={{
                                        backgroundColor: "#F37A20",
                                        color: "#fff",
                                        fontSize: "12px",
                                        padding: "4px 14px",
                                        borderRadius: "8px",
                                        border: "none",
                                        fontWeight: "600",
                                        whiteSpace: "nowrap",
                                        minWidth: "64px",
                                      }}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setSelectedCustomer(cust);
                                        setShowManualFields(false);
                                        setDropdownOpen(false);
                                      }}
                                    >
                                      Select
                                    </button>
                                  </div>
                                );
                              })}
                            </>
                          ) : (
                            <div className="p-3 text-center text-muted small">
                              No customer found matching "{searchQuery}"
                            </div>
                          )}

                          {/* Option to enter customer details manually */}
                          <div
                            className="border-top pt-2 mt-1 text-center"
                            style={{ cursor: "pointer" }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setShowManualFields(true);
                              setSelectedCustomer(null);
                              setDropdownOpen(false);
                            }}
                          >
                            <span className="small fw-semibold" style={{ color: "#F37A20" }}>
                              <i className="fi fi-rr-plus me-1"></i>
                              Enter guest details manually
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Manual Guest Entry Fields */}
                  {showManualFields && !selectedCustomer && (
                    <div className="p-3 mt-2 rounded-3 border" style={{ backgroundColor: "#FFF9F2", borderColor: "#FCE1CA" }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="small fw-bold text-dark">
                          <i className="fi fi-rr-user me-1" style={{ color: "#F37A20" }}></i> Guest Info
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm flex-shrink-0"
                          style={{
                            background: "#fff0f0",
                            color: "#e74c3c",
                            border: "none",
                            borderRadius: "20px",
                            fontWeight: 600,
                            padding: "4px 14px",
                            whiteSpace: "nowrap",
                            fontSize: "12px",
                          }}
                          onClick={() => {
                            setShowManualFields(false);
                            setManualCustomer({ name: "", email: "", phone: "" });
                            setManualError("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>

                      {manualError && (
                        <div className="alert alert-danger py-1 px-2 small mb-2">{manualError}</div>
                      )}

                      <div className="row g-2">
                        <div className="col-12 col-sm-6">
                          <label className="form-label small text-muted mb-1">Guest Name</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="e.g. John Doe"
                            value={manualCustomer.name}
                            onChange={(e) =>
                              setManualCustomer({ ...manualCustomer, name: e.target.value })
                            }
                          />
                        </div>
                        <div className="col-12 col-sm-6">
                          <label className="form-label small text-muted mb-1">
                            Guest Email <span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            placeholder="e.g. guest@example.com"
                            value={manualCustomer.email}
                            onChange={(e) =>
                              setManualCustomer({ ...manualCustomer, email: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* When input empty and no customer selected, show Enter Manually button */}
                  {!selectedCustomer && !showManualFields && !dropdownOpen && (
                    <div className="mt-2">
                      <button
                        type="button"
                        className="btn btn-sm w-100 py-2"
                        style={{
                          border: "1.5px dashed #F37A20",
                          color: "#F37A20",
                          background: "#FFF9F5",
                          borderRadius: "10px",
                          fontWeight: 600,
                          fontSize: "13px",
                        }}
                        onClick={() => setShowManualFields(true)}
                      >
                        <i className="fi fi-rr-user-add me-2"></i>
                        Enter Guest Details Manually
                      </button>
                    </div>
                  )}
                  {/* Top-level validation error (e.g. no guest added) */}
                  {manualError && !showManualFields && (
                    <div className="text-danger small mt-2">
                      <i className="fi fi-rr-exclamation me-1"></i>{manualError}
                    </div>
                  )}
                </div>

                {/* Bill Amount Section */}
                <div className="form-group enter-bill-box mb-4">
                  <h6 className="h6-headline text-center mb-3">Enter your bill amount</h6>
                  <div className="position-relative">
                    <input
                      type="number"
                      step="0.01"
                      className="form-control no_spring_input text-center fs-3 fw-bold"
                      id="amount"
                      name="amount"
                      placeholder="0.00"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.amount}
                      autoFocus
                    />
                  </div>
                  {formik.touched.amount && formik.errors.amount ? (
                    <div className="text-danger text-center small mt-1">{formik.errors.amount}</div>
                  ) : null}
                </div>

                {/* Payment Summary Section */}
                <h6 className="h6-headline text-left mb-2">Payment Summary</h6>
                <div className="payment-lis-des">
                  <div className="payment-summry">
                    <p className="text-mini">Bill amount</p>
                    <p className="subtitle">£{billAmount.toFixed(2)}</p>
                  </div>
                  <div className="payment-summry">
                    <p className="text-mini">Discount ({offerPercentage}%)</p>
                    <p className="subtitle text-success">-£{discount.toFixed(2)}</p>
                  </div>
                  {(selectedCustomer || manualCustomer.email) && (
                    <div className="payment-summry">
                      <p className="text-mini">Guest</p>
                      <p className="subtitle text-truncate" style={{ maxWidth: "220px" }}>
                        {selectedCustomer
                          ? selectedCustomer.email || selectedCustomer.fullName || selectedCustomer.name
                          : manualCustomer.email}
                      </p>
                    </div>
                  )}
                  <div className="payment-summry border-top pt-2 mt-1">
                    <p className="h6-headline">To pay</p>
                    <p className="subtitle fw-bold fs-5 text-dark">£{toPay.toFixed(2)}</p>
                  </div>
                </div>

                {/* Submit button */}
                <div className="text-center mt-4">
                  <button
                    className="btn btn-oring text-center px-4 py-2"
                    type="submit"
                    disabled={formik.isSubmitting || billAmount <= 0}
                  >
                    {formik.isSubmitting ? "Processing..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OfflinePayment;